import { MappedExchangeRates } from 'api/exchange-rate'
import { Currencies, Currency } from 'constants/currencies'
import { convertValue, getCurrencySymbol } from 'utils/currencies'

export const NumericMenusLabelsMap = Object.freeze({
  lowestAsk: {
    all: 'All',
    '20_to_1000': '{{currency}}0-{{currency}}1,000',
    '1000_to_3000': '{{currency}}1,000-{{currency}}3,000',
    '3000_to_5000': '{{currency}}3,000-{{currency}}5,000',
    '5000_to_10000': '{{currency}}5,000-{{currency}}10,000',
    '10000_plus': '{{currency}}10,000+',
  },
})

export function getLowestAskLabels(currency: Currency) {
  const symbol = getCurrencySymbol(currency)

  return Object.fromEntries(
    Object.entries(NumericMenusLabelsMap.lowestAsk).map(([key, value]) => [
      key,
      value.replace(/\{\{currency\}\}/g, symbol),
    ])
  ) as typeof NumericMenusLabelsMap.lowestAsk
}

export const NumericMenusMap = Object.freeze({
  lowestAsk: {
    attribute: 'lowestAsk',
    items: [
      {
        label: NumericMenusLabelsMap.lowestAsk.all,
        start: undefined,
        end: undefined,
      },
      {
        label: NumericMenusLabelsMap.lowestAsk['20_to_1000'],
        start: 2000,
        end: 100000,
      },
      {
        label: NumericMenusLabelsMap.lowestAsk['1000_to_3000'],
        start: 100000,
        end: 300000,
      },
      {
        label: NumericMenusLabelsMap.lowestAsk['3000_to_5000'],
        start: 300000,
        end: 500000,
      },
      {
        label: NumericMenusLabelsMap.lowestAsk['5000_to_10000'],
        start: 500000,
        end: 1000000,
      },
      {
        label: NumericMenusLabelsMap.lowestAsk['10000_plus'],
        start: 1000000,
        end: undefined,
      },
    ],
  },
})

export function getLowestAskItems(
  currency: Currency,
  exchange: MappedExchangeRates | undefined | null
) {
  const labels = getLowestAskLabels(currency)

  const convert = (value: number | undefined) => {
    if (value === undefined) return undefined

    return convertValue(value, {
      from: currency,
      to: Currencies.GBP,
      exchange,
      userProfile: undefined,
      ipInfo: undefined,
      selectedCurrency: undefined,
    })
  }

  return [
    {
      label: labels['20_to_1000'],
      start: convert(2000),
      end: convert(100000),
    },
    {
      label: labels['1000_to_3000'],
      start: convert(100100),
      end: convert(300000),
    },
    {
      label: labels['3000_to_5000'],
      start: convert(300100),
      end: convert(500000),
    },
    {
      label: labels['5000_to_10000'],
      start: convert(500100),
      end: convert(1000000),
    },
    {
      label: labels['10000_plus'],
      start: convert(1000100),
      end: Number.MAX_SAFE_INTEGER,
    },
  ]
}

export const RefinementListLabelsMap = Object.freeze({
  medium: {
    print: 'Print',
    sculpture: 'Sculpture',
    collectible: 'Collectible',
    photography: 'Photography',
    book: 'Book',
  },

  nationalities: {
    american: 'American',
    british: 'British',
    irish: 'Irish',
    chinese: 'Chinese',
    french: 'French',
    german: 'German',
    italian: 'Italian',
    spanish: 'Spanish',
    japanese: 'Japanese',
  },

  material: {
    ceramic: 'Ceramic',
    digital: 'Digital',
    etching: 'Etching',
    glass: 'Glass',
    inkjet: 'Inkjet',
    letterpress: 'Letterpress',
    linocut: 'Linocut',
    lithograph: 'Lithograph',
    plush: 'Plush',
    polystone: 'Polystone',
    porcelain: 'Porcelain',
    resin: 'Resin',
    screenprint: 'Screenprint',
    vinyl: 'Vinyl',
    woodcut: 'Woodcut',
  },
})

export const RefinementListsMap = Object.freeze({
  medium: {
    attribute: 'medium',
  },

  nationalities: {
    attribute: 'artists.nationalities',
  },

  material: {
    attribute: 'materialFilter',
  },

  size: {
    attribute: 'size',
  },
})
